import { layoutUnits, findFeeder } from './tree';
import { geo, placeUnits, placeExtras, finalize, winnerName, roundName } from './geometry';

const markTree = (node, tag, map) => {
  if (!node) return;
  map.set(node.match.id, tag);
  node.kids.forEach((k) => markTree(k, tag, map));
};

const headersForCards = (g, cards, y, nameOf, o = {}) => {
  const cols = new Map();
  cards.forEach((c) => { if (!cols.has(c.col)) cols.set(c.col, c); });
  cols.forEach((c, col) => g.header(col, y, nameOf(col), { pg: c.pg, ...o }));
};

// Single elimination — mirrored championship: both halves converge on a
// centred final with the champion card directly beneath it.
export function buildMirrored(model, M, pages) {
  const g = geo(M);
  const R = model.rounds - 1;
  const rank = (m) => model.rank.get(m.round ?? 0);
  const top = g.top(0);
  const [lk, rk] = model.root.kids;
  const uL = new Map();
  const uR = new Map();
  layoutUnits(lk, uL);
  layoutUnits(rk, uR);
  const dy = uR.get(rk.match.id).y - uL.get(lk.match.id).y;
  const left = placeUnits(g, uL, { colOf: rank, top: top + Math.max(0, dy) * M.slotH, dir: 1, pgOf: () => 'L', byeText: 'BYE' });
  const right = placeUnits(g, uR, { colOf: (m) => 2 * R - rank(m), top: top + Math.max(0, -dy) * M.slotH, dir: -1, pgOf: () => 'R', byeText: 'BYE' });
  const lRoot = left.get(lk.match.id);
  const fin = g.card(model.root.match, R, lRoot.y + M.cardH / 2, { pg: 'C', tone: 'final', label: 'FINAL' });
  g.connect(lRoot, fin, 0, 1);
  g.connect(right.get(rk.match.id), fin, 1, -1);
  const champ = g.champion(R, fin.y + fin.h + M.fs * 1.6 + g.champH / 2, { pg: 'C', name: winnerName(model.root.match) });
  const cx = fin.x + fin.w / 2;
  g.line([[cx, fin.y + fin.h], [cx, champ.y]], { tone: 'gold', done: !!model.root.match.winner, pg: 'C' });
  headersForCards(g, left, 0, (col) => roundName(R - col));
  headersForCards(g, right, 0, (col) => roundName(col - R));
  g.header(R, 0, 'CHAMPIONSHIP', { pg: 'C', tone: 'final' });
  placeExtras(g, model.extras, pages === 2 ? 'R' : 'C');
  return finalize(g, pages === 2 ? [['L', 'C'], ['R', 'C']] : null);
}

// Single elimination — classic left-to-right tree.
export function buildClassic(model, M, pages) {
  const g = geo(M);
  const R = model.rounds - 1;
  const { root } = model;
  const top = g.top(0);
  const two = pages === 2 && !!root.kids[0] && !!root.kids[1];
  const units = new Map();
  layoutUnits(root, units, { rootGap: two ? 1.8 : 0 });
  const side = new Map();
  if (two) { markTree(root.kids[0], 'T', side); markTree(root.kids[1], 'B', side); }
  const cards = placeUnits(g, units, {
    colOf: (m) => model.rank.get(m.round ?? 0), top, dir: 1, byeText: 'BYE',
    pgOf: (id) => side.get(id) || 'C', toneOf: (id) => (id === root.match.id ? 'final' : 'normal'),
  });
  const fin = cards.get(root.match.id);
  fin.label = 'FINAL';
  const ry = g.rowsY(fin);
  const champ = g.champion(R + 1, ry, { pg: 'C', name: winnerName(root.match) });
  g.line([[fin.x + fin.w, ry], [champ.x, ry]], { tone: 'gold', done: !!root.match.winner, pg: 'C' });
  const early = [...cards.values()].filter((c) => c.col < R);
  const topPg = two ? 'T' : 'C';
  headersForCards(g, early, 0, (col) => roundName(R - col), { pg: topPg });
  g.header(R, 0, 'FINAL', { pg: topPg, tone: 'final' });
  g.header(R + 1, 0, 'CHAMPION', { pg: topPg, tone: 'champ' });
  if (two) {
    const bCards = early.filter((c) => c.pg === 'B');
    if (bCards.length) headersForCards(g, bCards, Math.min(...bCards.map((c) => c.y)) - M.headerH, (col) => roundName(R - col), { pg: 'B' });
  }
  placeExtras(g, model.extras, two ? 'B' : 'C');
  return finalize(g, two ? [['T', 'C'], ['B', 'C']] : null);
}

// Double elimination — winners bracket stacked over losers bracket, both
// feeding a grand final on the right.
export function buildDouble(model, M, pages) {
  const g = geo(M);
  const { wb, lb, gf } = model;
  const two = pages === 2 && !!lb;
  const wbCols = wb.rank.size;
  const uW = new Map();
  const wbUnits = layoutUnits(wb.root, uW);
  const wbTop = g.top(0);
  const wbCards = placeUnits(g, uW, { colOf: (m) => wb.rank.get(m.round ?? 0), top: wbTop, dir: 1, pgOf: () => 'T', byeText: 'BYE' });
  headersForCards(g, wbCards, 0, (col) => (col === wbCols - 1 ? 'WB FINAL' : `WB ROUND ${col + 1}`));
  const wbRoot = wbCards.get(wb.root.match.id);
  let lbRoot = null;
  let lbCols = 0;
  let lbHeaderY = 0;
  if (lb) {
    lbCols = lb.rank.size;
    lbHeaderY = wbTop + wbUnits * M.slotH + M.fs * 2.4;
    const uL = new Map();
    layoutUnits(lb.root, uL);
    const lbCards = placeUnits(g, uL, {
      colOf: (m) => lb.rank.get(m.round ?? 0), top: g.top(lbHeaderY), dir: 1, pgOf: () => 'B', toneOf: () => 'lb',
    });
    headersForCards(g, lbCards, lbHeaderY, (col) => (col === lbCols - 1 ? 'LB FINAL' : `LB ROUND ${col + 1}`), { tone: 'lb' });
    const firstLb = g.items.find((it) => it.type === 'header' && it.tone === 'lb');
    if (firstLb) firstLb.testId = 'lb-section-label';
    if (!two) {
      const dvY = lbHeaderY - M.fs * 1.2;
      g.line([[0, dvY], [g.colX(Math.max(wbCols, lbCols) + 1) - M.colGap, dvY]], { tone: 'divider', pg: 'C' });
    }
    lbRoot = lbCards.get(lb.root.match.id);
  }
  if (gf) {
    const gfCol = Math.max(wbCols, lbCols);
    const ry = !lbRoot ? g.rowsY(wbRoot) : two ? g.rowsY(lbRoot) : (g.rowsY(wbRoot) + g.rowsY(lbRoot)) / 2;
    const pg = two ? 'B' : 'C';
    const gfCard = g.cardAtRows(gf, gfCol, ry, { pg, tone: 'final', label: 'GRAND FINAL' });
    if (two) {
      const wy = g.rowsY(wbRoot);
      const wx = wbRoot.x + wbRoot.w;
      g.line([[wx, wy], [wx + M.colGap * 0.7, wy]], { done: !!wbRoot.match.winner, pg: 'T' });
      g.note(wx + M.colGap * 0.8, wy + M.fs * 0.2, 'TO GRAND FINAL (PAGE 2)', { pg: 'T', size: M.fs * 0.6, bold: true });
    } else g.connect(wbRoot, gfCard, 0, 1);
    if (lbRoot) g.connect(lbRoot, gfCard, 1, 1);
    const champ = g.champion(gfCol, gfCard.y + gfCard.h + M.fs * 1.4 + g.champH / 2, { pg, name: winnerName(gf) });
    const cx = gfCard.x + gfCard.w / 2;
    g.line([[cx, gfCard.y + gfCard.h], [cx, champ.y]], { tone: 'gold', done: !!gf.winner, pg });
    g.header(gfCol, two ? lbHeaderY : 0, 'GRAND FINAL', { pg, tone: 'final' });
  }
  placeExtras(g, model.extras, two ? 'B' : 'C');
  return finalize(g, two ? [['T'], ['B']] : null);
}

// 3-game guarantee: quarterfinals in the middle, winners flow right toward
// the championship and losers flow left toward 5th place.
export function buildConsolation(model, M) {
  const g = geo(M);
  const top = g.top(0);
  const COL = { fifth: 0, cb: 1, qf: 2, wb: 3, champ: 4 };
  const qfCards = new Map(model.qf.map((m, i) => [m.id, g.card(m, COL.qf, top + (i + 0.5) * M.slotH)]));
  const center = top + (model.qf.length / 2) * M.slotH;
  const placeFed = (m, col, pool, map, kinds, fallback, dir, o) => {
    const feeds = [m.team_a, m.team_b]
      .map((t, s) => [s, findFeeder(t, pool, m.round ?? 99, kinds)])
      .filter(([, f]) => f && map.get(f.match.id))
      .map(([s, f]) => [s, map.get(f.match.id)]);
    const cy = feeds.length ? feeds.reduce((a, [, c]) => a + c.y + M.cardH / 2, 0) / feeds.length : fallback;
    const card = g.card(m, col, cy, o);
    feeds.forEach(([s, c]) => g.connect(c, card, s, dir, { dashed: kinds.includes('L') }));
    return card;
  };
  const pairY = (i) => top + (2 * i + 1) * M.slotH;
  const wbMap = new Map(model.wbSf.map((m, i) => [m.id, placeFed(m, COL.wb, model.qf, qfCards, ['W'], pairY(i), 1, {})]));
  const cbMap = new Map(model.cbSf.map((m, i) => [m.id, placeFed(m, COL.cb, model.qf, qfCards, ['L'], pairY(i), -1, { tone: 'lb' })]));
  const below = (card) => card.y + card.h + M.fs * 1.4 + g.champH / 2;
  if (model.champ) {
    const c = placeFed(model.champ, COL.champ, model.wbSf, wbMap, ['W'], center, 1, { tone: 'final', label: 'CHAMPIONSHIP' });
    const ch = g.champion(COL.champ, below(c), { name: winnerName(model.champ) });
    g.line([[c.x + c.w / 2, c.y + c.h], [c.x + c.w / 2, ch.y]], { tone: 'gold', done: !!model.champ.winner });
  }
  if (model.fifth) {
    const c = placeFed(model.fifth, COL.fifth, model.cbSf, cbMap, ['W'], center, -1, { tone: 'placement', label: '5TH PLACE' });
    const ch = g.champion(COL.fifth, below(c), { name: winnerName(model.fifth), title: '5TH PLACE', tone: 'silver' });
    g.line([[c.x + c.w / 2, c.y + c.h], [c.x + c.w / 2, ch.y]], { done: !!model.fifth.winner, tone: 'lb' });
  }
  const bandY = top + model.qf.length * M.slotH + M.fs * 2 + M.cardH / 2;
  if (model.third) g.card(model.third, COL.wb, bandY, { tone: 'placement', label: '3RD PLACE' });
  if (model.seventh) g.card(model.seventh, COL.cb, bandY, { tone: 'placement', label: '7TH PLACE' });
  const heads = [['fifth', '5TH PLACE', 'lb'], ['cb', 'CONSOLATION', 'lb'], ['qf', 'QUARTERFINALS'], ['wb', 'WINNERS SEMIS'], ['champ', 'CHAMPIONSHIP', 'final']];
  heads.forEach(([k, text, tone]) => {
    if (g.items.some((it) => it.type === 'match' && it.col === COL[k])) g.header(COL[k], 0, text, { tone: tone || 'normal' });
  });
  placeExtras(g, model.extras, 'C');
  return finalize(g, null);
}
